import { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface Project {
  title: string;
  description: string;
  image: string;
  tags: string[];
  link: string;
}

const projects: Project[] = [
  {
    title: 'Trifinder - Job Finding Platform',
    description: 'Designed and developed a job finding platform that connects job seekers with employers, streamlining the recruitment process and enhancing career opportunities for both parties.',
    image: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="none"><rect width="100" height="100" fill="%230070f3"/><text x="50" y="50" font-family="Arial" font-size="12" fill="white" text-anchor="middle" dy=".3em">Trifinder</text></svg>',
    tags: ['TypeScript', 'React', 'Node.js', 'Azure', 'PostgreSQL', 'Docker'],
    link: 'https://trifinder.trifusion.chat'
  },
  {
    title: 'Trifusion AI - Corporate AI',
    description: 'Built a cutting-edge corporate AI platform that leverages advanced machine learning algorithms to provide actionable insights, optimize business processes, and drive informed decision-making.',
    image: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="none"><rect width="100" height="100" fill="%2300b4d8"/><text x="50" y="50" font-family="Arial" font-size="12" fill="white" text-anchor="middle" dy=".3em">TrifusionAI</text></svg>',
    tags: ['React', 'TypeScript', 'Node.js', 'PostgreSQL', 'Docker', 'ClaudeAPI'],
    link: 'https://ai.trifusion.chat'
  },
  {
    title: 'Tripayments - ePOS System & Software Solution',
    description: 'Designed and developed a secure, user-friendly ePOS system that streamlines point-of-sale operations for restaurants, cafes, and other service establishments, enhancing customer satisfaction and operational efficiency.',
    image: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="none"><rect width="100" height="100" fill="%231a1a1a"/><text x="50" y="50" font-family="Arial" font-size="12" fill="white" text-anchor="middle" dy=".3em">Tripayments</text></svg>',
    tags: ['PHP', 'Node.js', 'PostgreSQL', 'Stripe', 'HTML', 'CSS', 'JavaScript'],
    link: 'https://payments.trifusion.chat'
  }
];

export default function Projects() {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const projectVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="projects" className="py-20 bg-gradient-to-br from-indigo-800 via-gray-800 to-purple-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            Featured <span className="bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">Projects</span>
          </motion.h2>
          <div className="w-20 h-1 bg-gradient-to-r from-purple-300 to-indigo-300 mx-auto rounded-full"></div>
        </div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {projects.map((project, index) => (
            <motion.div
              key={index}
              variants={projectVariants}
              whileHover={{ y: -10 }}
              className="bg-gradient-to-br from-gray-700/50 to-purple-700/30 rounded-2xl overflow-hidden shadow-lg border border-purple-400/20 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-400/20 hover:border-purple-200/40 backdrop-blur-sm"
            >
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">
                  {project.title}
                </h3>
                <p className="text-gray-200 mb-4">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-3 py-1 bg-gradient-to-r from-purple-600/30 to-indigo-600/30 text-purple-200 rounded-full text-sm border border-purple-400/30"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <a
                  href={project.link}
                  className="inline-block bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-purple-400/25 transform hover:scale-105"
                >
                  View Project
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}